import { useState } from 'react';

import { ChevronLeft, ExternalLink, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Form } from '@/components/ui/form';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import { getBillStatusText } from '@/modules/common/components/molecules/badge/status-configs/bill-status-config';
import { FullPageErrorState } from '@/modules/common/components/organisms/full-page-error-state';
import { QUICKBOOKS_BILL_URL } from '@/modules/finance/common/constants/quickbooks-urls';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';

import { BillDetailsForm } from '../components/details/bill-details-form';
import { BillDetailsSkeleton } from '../components/details/bill-details-skeleton';
import { BillErrorModal } from '../components/modals/bill-error-modal';
import { useBillDetailsPage, useRetryBill } from '../hooks';
import { BillAllowedAction } from '../types';

export function BillDetailsPage() {
  const { t } = useTranslation(['authenticated.bills', 'common']);
  const controller = useBillDetailsPage();
  const { details } = controller;
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const retryMutation = useRetryBill();

  if (controller.isError || (!controller.isLoading && !details)) {
    return (
      <FullPageErrorState
        title={t('details.error.title')}
        description={t('details.error.description')}
        actions={<Button onClick={controller.navigateBack}>{t('details.error.back')}</Button>}
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <header className="z-[5] shrink-0 border-b border-border bg-surface px-4 py-3 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 shrink-0 p-0"
              aria-label={t('common:back')}
              onClick={controller.navigateBack}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h1 className="truncate text-xl font-semibold sm:text-2xl">
              {details?.referenceNumber
                ? t('details.title', { referenceNumber: details.referenceNumber })
                : t('details.titleWithoutReference')}
            </h1>
            {details && (
              <div className="flex items-center gap-1">
                <StatusBadge
                  type={StatusBadgeType.Bill}
                  status={details.status}
                  label={getBillStatusText(details.status)}
                />
                {details.syncError && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={t('details.viewError')}
                    onClick={() => setIsErrorModalOpen(true)}
                  >
                    <InfoIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          {details?.externalId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(QUICKBOOKS_BILL_URL(details.externalId as string), '_blank')
              }
            >
              <ExternalLink />
              {t('details.openInQuickBooks')}
            </Button>
          )}
        </div>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-6">
        {controller.isLoading && <BillDetailsSkeleton />}
        {details && (
          <Form {...controller.form}>
            <form id="bill-details-form" onSubmit={controller.submit}>
              <BillDetailsForm
                details={details}
                form={controller.form}
                isReadOnly={controller.isReadOnly}
                monthlySalaryAvailability={controller.monthlySalaryAvailability}
                isMonthlySalaryAvailabilityLoading={controller.isMonthlySalaryAvailabilityLoading}
              />
            </form>
          </Form>
        )}
      </main>
      {!controller.isReadOnly && (
        <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-surface px-4 py-3">
          <Button
            variant="outline"
            onClick={controller.cancelEdit}
            disabled={!controller.form.formState.isDirty || controller.isUpdating}
          >
            {t('common:cancel')}
          </Button>
          <ButtonLoadable
            type="submit"
            form="bill-details-form"
            isLoading={controller.isUpdating}
            disabled={
              !controller.form.formState.isDirty ||
              !controller.form.formState.isValid ||
              controller.isTotalNegative
            }
          >
            {t('details.edit.save')}
          </ButtonLoadable>
        </footer>
      )}
      <UnsavedChangesModal
        open={controller.unsavedChanges.isModalOpen}
        onClose={controller.unsavedChanges.handleModalCancel}
        onDiscard={controller.unsavedChanges.handleModalDiscard}
      />
      <BillErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        syncOperation={details?.syncOperation}
        syncError={details?.syncError}
        onRetry={
          details?.allowedActions.includes(BillAllowedAction.Retry)
            ? () => details && retryMutation.mutate(details.id)
            : undefined
        }
        isRetrying={retryMutation.isPending}
      />
    </div>
  );
}
